/* eslint-disable no-unused-vars */
import React from 'react';
import AuthorizeService from '../ApiAuthorization/AuthorizeService';
import AppConfig from "../Configuration/AppConfig";
import { showError } from "../PageLayout/ErrorToast";
import HttpErrorMessages from '../TechnicalPage/ErrorPageHandler/HttpErrorMessages/HttpErrorMessages';

export class ApiService {
  static errorDisplayDelay = 1000;

  async StaticFetch(relativePath, httpMethod) {
    const frontHost = AppConfig.Network.AppBaseUrl;
    const urlFront = `${frontHost}/${relativePath}`;
    const params = {
      method: httpMethod
    };

    const response = await fetch(urlFront, params);
    if (!response.ok) {
      return Promise.reject({
        response,
        errorType: 'httpStatus',
        errorDisplayTimer: setTimeout(() => this.handleApiError(response), ApiService.errorDisplayDelay),
      });
    }

    return response;
  }

  handleApiError(response) {
    let content;
    let asContent = !(response && response.text && !response.bodyUsed);

    if (asContent) {
      content = response.text();
    } else {
      content = Promise.resolve();
    }

    content.then(errorMessage => {
      if (!asContent || errorMessage === '') {
        errorMessage = 'No message';
      }
      const errorTitle = `CoDAI API: ${response.status} ${response.statusText}`;
      const errorMsg = `${response.url}:\n${errorMessage}`;
      console.warn(errorTitle, errorMsg, response);
      showError(errorTitle, errorMsg, <HttpErrorMessages httpCode={response.status} />);
    });
  }

  async Fetch(relativePath, httpMethod, body) {
    const token = AuthorizeService.getAccessToken();
    const apiHost = AppConfig.Network.webApi;
    const urlApiTests = `${apiHost}/api/${relativePath}`;
    const params = {
      method: httpMethod,
      body: (body ? JSON.stringify(body) : null),
      mode: 'cors',
      credentials: 'include',
      headers: !token ? {} : {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Access-Control-Allow-Credentials': 'true',
        Accept: 'application/json'
      }
    };

    const response = await fetch(urlApiTests, params);
    if (!response.ok) {
      return Promise.reject({
        response,
        errorType: 'httpStatus',
        errorDisplayTimer: setTimeout(() => this.handleApiError(response), ApiService.errorDisplayDelay),
      });
    }

    return response;
  }

  async baseFetch(relativePath, httpMethod, body) {
    let response = await this.Fetch(relativePath, httpMethod, body);

    let next;
    try {
      let textRep = await response.text();
      next = textRep.length ? JSON.parse(textRep) : {};
      return next;
    } catch (e) {
      const ce = new Error(e.message);
      ce.response = response;
      ce.errorType = 'jsonParse';
      ce.errorDisplayTimer = setTimeout(() => {
        const errorTitle = `CoDAI API: ${response.status} ${response.statusText}`;
        const errorMsg = `${response.url} JSON parsing error: ${e.message}`;
        console.warn(errorTitle, errorMsg, response);
        showError(errorTitle, errorMsg, <HttpErrorMessages httpCode={response.status} />);
      }, ApiService.errorDisplayDelay);
      throw ce;
    }
  }

  async blobFetch(relativePath, httpMethod, body) {
    let response = await this.Fetch(relativePath, httpMethod, body);

    let next;
    try {
      next = response.blob();
      await next;
    } catch (e) {
      next = Promise.reject({
        response,
        errorType: 'blobParse',
        errorDisplayTimer: setTimeout(() => {
          const errorTitle = `CoDAI API: ${response.status} ${response.statusText}`;
          const errorMsg = `${response.url} Blob error: ${e.message}`;
          console.warn(errorTitle, errorMsg, response);
          showError(errorTitle, errorMsg, <HttpErrorMessages httpCode={response.status} />);
        }, ApiService.errorDisplayDelay),
      });
    }

    return next;
  }

  async getCreateProjectRequest(requestId) {
    return this.baseFetch(`projectcreationrequest/${requestId}`, 'GET');
  }

  async createCreateProjectRequest(projectCreationRequest) {
    return this.baseFetch(`projectcreationrequest`, 'POST', projectCreationRequest);
  }

  async updateCreateProjectRequest(projectCreationRequest) {
    return this.baseFetch(`projectcreationrequest`, 'PUT', projectCreationRequest);
  }

  async createProjectRequestListAll() {
    return this.baseFetch(`projectcreationrequest?onlyMine=false`, 'GET');
  }
  async createProjectRequestListMine() {
    return this.baseFetch(`projectcreationrequest`, 'GET');
  }
  async userSearch(inputTxt) {
    return this.baseFetch(`user/search/${inputTxt}`, 'GET');
  }

  async userGroups() {
    return this.baseFetch(`user/groups`, 'GET');
  }

  async userPhoto() {
    return this.baseFetch(`user/photo`, 'GET');
  }

  async getUserPrefProjects() {
    return this.baseFetch(`user/pref/project`, 'GET');
  }
  async addUserPrefProjects(id) {
    return this.baseFetch(`user/pref/project/${id}`, 'POST');
  }
  async deleteUserPrefProjects(id) {
    return this.baseFetch(`user/pref/project/${id}`, 'DELETE');
  }

  async checkUserAmlCIConsent() {
    return this.baseFetch(`user/consent/computeinstance`, 'GET');
  }

  async checkUserCodaiConsent() {
    return this.baseFetch(`user/consent/codai`, 'GET');
  }
  async grantUserCodaiConsent() {
    return this.baseFetch(`user/consent/codai`, 'POST');
  }


  async projectSearch(search) {
    return this.baseFetch(`project/filter`, 'POST', search);
  }

  async getProject(id) {
    return this.baseFetch(`project/${id}`, 'GET');
  }

  async getProjectDetails(id) {
    return this.baseFetch(`project/${id}/details`, 'GET');
  }

  async getProjectStarred() {
    return this.baseFetch(`project/starred`, 'GET');
  }

  async getProjectGlobalStarred() {
    const search = {
      orderBy: "globalStar",
      limit: 5
    };
    return this.projectSearch(search);

  }
  async getProjectRepositoryFile(id, file) {
    return this.blobFetch(`project/${id}/repositoryFile?file=${encodeURIComponent(file)}`, 'GET');
  }

  async updateProject(project) {
    return this.baseFetch(`project`, 'PUT', project);
  }

  async rebootWebAppAndDoc(project) {
    return this.baseFetch(`project/rebootWebappAndDoc`, 'POST', project);
  }

  async createProjectComputeInstance(projectId, computeInstance) {
    return this.baseFetch(`project/${projectId}/compute`, 'POST', computeInstance);
  }

  async setStateProjectComputeInstance(projectId, computeName, toActivate) {
    return this.baseFetch(`project/${projectId}/compute/${computeName}/state/${toActivate}`, 'POST');
  }

  async deleteProjectComputeInstance(projectId, computeName) {
    return this.baseFetch(`project/${projectId}/compute/${computeName}`, 'DELETE');
  }
  // ApiService.updateProjectCiSchedule(this.state.projectDetail.projectId, instance.name, currentSchedule)
  async updateProjectCiSchedule(projectId, computeName, schedule) {
    return this.baseFetch(`project/${projectId}/compute/${computeName}/schedule`, 'POST', schedule);
  }
  async getRef() {
    let ref = await this.baseFetch(`refs`, 'GET');

    ref.tagRefs = ref.tagRefs?.sort((a, b) => a.tagTypeRef.name.localeCompare(b.tagTypeRef.name) || a.name.localeCompare(b.name)) ?? [];
    ref.profileTypeRefs = ref.profileTypeRefs?.map(c => ({
      value: c.profileTypeRefId,
      displayName: c.profileTypeRefDescription
    })) ?? [];

    return ref;
  }

  async searchUser(partialUpn) {
    const search = encodeURIComponent(partialUpn);
    return this.baseFetch(`user/search/` + search, 'GET');
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

}

const apiService = new ApiService();

export default apiService;
